// use this Select
import React, { ReactText } from 'react';
import { useSelect, UseSelectProps } from 'downshift';
import styled from 'styled-components';
import { themeGet } from 'styled-system';
import { ChevronDown } from 'styled-icons/fa-solid/ChevronDown';

import { capitalizeFirstLetter } from 'src/helpers';
import { Text } from 'src/kit';
import { BaseBox, Box } from './Box';

export function Dropdown({
  items = [],
  width,
  label,
  isFetching,
  placeholder,
  ...props
}: UseSelectProps<{ value: string | number; label: ReactText }> & {
  width?: number | string;
  placeholder?: ReactText;
  label?: ReactText;
  isFetching?: boolean;
}) {
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps
  } = useSelect({
    items,
    itemToString: item => String(item?.label),
    ...props
  });

  return (
    <Wrapper width={width} label={label} isOpen={isOpen}>
      {label && (
        <Text className="label" color="textSecondary">
          {label}
        </Text>
      )}
      <div className="input-box" {...getToggleButtonProps()}>
        <Text textStyle="ellipsis" as="label" color="textSecondary" fontSize="14px" {...getLabelProps()}>
          {isFetching ? 'Loading...' : capitalizeFirstLetter(selectedItem?.label || placeholder || ' ')}
        </Text>
        <BaseBox
          as={ChevronDown}
          alignSelf="center"
          ml="8px"
          size={'12px'}
          color="textSecondary"
          className="chevron"
        ></BaseBox>
      </div>
      {isOpen && (
        <ul className="menu" {...getMenuProps()}>
          {items.map((item, index) => (
            <MenuItem {...{ highlightedIndex, index }} {...getItemProps({ item, index })} key={item?.value || index}>
              <Text
                lineHeight="34px"
                style={{ whiteSpace: 'nowrap' }}
                px={20}
                color="textSecondary"
                fontSize={14}
                fontWeight="normal"
                textStyle="ellipsis"
              >
                {capitalizeFirstLetter(item?.label || '')}
              </Text>
            </MenuItem>
          ))}
        </ul>
      )}
    </Wrapper>
  );
}

const Wrapper = styled(Box)<{ isOpen: boolean; label: ReactText }>`
  min-width: 200px;
  z-index: 10;
  position: relative;
  .label {
    margin-bottom: 10px;
    font-size: 12px;
  }
  .input-box {
    padding: 11px 12px;
    border: 1px solid ${themeGet('colors.iceBlueTwo')};
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    &:hover {
      border-color: ${({ isOpen }) => (isOpen ? themeGet('colors.primary') : themeGet('colors.cloudyBlueTwo'))};
    }
    border-color: ${({ isOpen }) => isOpen && themeGet('colors.primary')};

    > label {
      height: 16px;
    }
  }

  .chevron {
    transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0)')};
  }

  .menu {
    position: absolute;
    top: calc(${({ label }) => (label ? '30' : '6')}px + 42px);
    max-height: 200px;
    overflow-x: hidden;
    overflow-y: auto;
    z-index: 1000000;
    margin: 0;
    padding: 6px 0;
    list-style: none;
    outline: none;
    width: 100%;
    height: auto;
    background-color: ${themeGet('colors.white')};
    border-radius: 4px;
    box-shadow: ${themeGet('shadows.dropdownMenu')};
    div {
      user-select: none;
    }
  }
`;

const MenuItem = styled.li<{ highlightedIndex: number; index: number }>`
  height: 34px;
  cursor: pointer;
  display: flex;
  background-color: ${({ highlightedIndex, index }) =>
    highlightedIndex === index ? themeGet('colors.combobox.selected') : themeGet('colors.white')};
  transition: background 0.2s cubic-bezier(0.4, 0, 1, 1);
`;
